import { OfferSaveErrorCode } from '@/api/ErrorCode.model.ts';
import {
  CustomerShortDto,
  OfferInstanceTermErrorDetailsDtoCodeEnum,
  ProcessCalendarEntryQueryDto,
  ProcessDto,
  ProcessOfferInstanceSummaryDto,
  ProcessStatusDto,
  ReservationOverlapTypeDto,
} from '@/api/generated';
import {
  CustomerTypeOptionsTranslation,
  customerTypeOptionsTranslation,
  processStatusOptionsTranslation,
  ProcessStatusTranslation,
} from '@/assets/locales/utils/dictionaries/dictionaries.translation.pl.ts';
import { ProcessDetailsTab } from '@/views/processesDetails/model/ProcessDetailsTab.enum.ts';

export type ProcessesDetailsTranslation = {
  addTask: {
    button: {
      open: string;
    };
  };
  customer: Record<keyof Omit<CustomerShortDto, 'customerId'>, string> & {
    noCustomerSelected: string;
    tooltip: {
      goToCustomer: string;
    };
    typeOptions: CustomerTypeOptionsTranslation;
  };
  header: {
    button: {
      edit: string;
    };
    calendarEntryQuery: {
      button: {
        cancel: string;
        clear: string;
        edit: string;
        editBookingModeError: string;
        save: string;
      };
      dialogTitle: string;
      summary: {
        resourceWithRange: string;
        typeWithEntry: string;
      };
      title: string;
      withoutRange: string;
    };
    nextStatus: ProcessStatusTranslation;
    status: string;
    statusOptions: ProcessStatusTranslation;
  };
  main: Record<
    keyof Omit<
      ProcessDto,
      | 'customer'
      | 'description'
      | 'name'
      | 'offerInstances'
      | 'processId'
      | 'requestedDictionaryName'
      | keyof ProcessCalendarEntryQueryDto
    >,
    string
  >;
  offerInstances: {
    actions: {
      open: string;
    };
    create: {
      button: {
        cancel: string;
        create: string;
      };
      offerDefinition: string;
      openButton: string;
      title: string;
    };
    details: {
      backAriaLabel: string;
      button: {
        cancel: string;
        save: string;
      };
      calendarEntryQueryMismatch: {
        entry: string;
        entryMany: string;
        hint: string;
        term: string;
        title: string;
      };
      components: {
        addDictionaryItem: string;
        addFileItem: string;
        addItem: string;
        addTextItem: string;
        assembly: {
          availability: Record<'assembly' | 'disassembly', string>;
          description: Record<'assembly' | 'disassembly', string>;
          from: string;
          hint: string;
          readFrom: string;
          readTo: string;
          title: string;
          to: string;
        };
        assignedPerson: string;
        assigneeTypeSupervisor: string;
        assigneeTypeUser: string;
        availability: {
          actions: string;
          available: string;
          clearFilters: string;
          close: string;
          closeDialog: string;
          empty: string;
          emptyFiltered: string;
          filterElements: string;
          filters: string;
          filterStatus: string;
          from: string;
          hintDetails: string;
          item: string;
          loadError: string;
          nextRange: string;
          prevRange: string;
          rangeOrder: string;
          rangeOutsideTerm: string;
          rangeRequired: string;
          refresh: string;
          reservation: {
            more: string;
            occupied: string;
            statusShort: {
              confirmed: string;
              tentative: string;
            };
          };
          retry: string;
          select: string;
          selectBlocked: string;
          selectBlockedByWindow: string;
          sharedReservationsWarning: string;
          status: {
            confirmed: string;
            free: string;
            tentative: string;
            windowOnly: string;
          };
          subtitle: string;
          subtitleAssembly: string;
          subtitleDisassembly: string;
          tableAriaLabel: string;
          tentativeWarning: string;
          termRequired: string;
          title: string;
          to: string;
          today: string;
        };
        changeFile: string;
        checkAvailability: string;
        complete: string;
        componentsCount: string;
        deleteItemAriaLabel: string;
        dictionaryDescription: string;
        dictionaryItemType: string;
        dictionaryValue: string;
        dictionaryVariants: string;
        editItem: string;
        editItemAriaLabel: string;
        empty: string;
        emptyFiltered: string;
        enabled: string;
        externalPrice: string;
        fileAdded: string;
        fileDescription: string;
        fileDownload: string;
        fileEmpty: string;
        fileRemove: string;
        fileValue: string;
        fillFromOfferTerm: string;
        fillText: string;
        inactive: string;
        inactiveItem: string;
        incomplete: string;
        internalPrice: string;
        itemCancel: string;
        itemName: string;
        itemSave: string;
        itemsTitle: string;
        itemValueEmpty: string;
        lastChange: string;
        missing: string;
        noReservation: string;
        noText: string;
        prices: string;
        progress: string;
        requiredDocuments: string;
        requiredForNextStatus: string;
        requiredForNextStatusTooltip: string;
        requiredForStatus: string;
        reservationFrom: string;
        reservationTerm: string;
        reservationTo: string;
        reservationValidation: {
          rangeOrder: string;
          rangeOutsideTerm: string;
          rangeRequired: string;
          termRequired: string;
        };
        returnToStatus: string;
        saveError: string;
        saveSuccess: string;
        textDescription: string;
        textFields: string;
        textValue: string;
        valueType: Record<'DICTIONARY' | 'FILE' | 'TEXT', string>;
      };
      header: {
        collapseAriaLabel: string;
        expandAriaLabel: string;
        preview: {
          ariaLabel: string;
          eventTerm: string;
          noEventTerm: string;
          termErrors: string;
          totalExternalPrice: string;
          totalInternalPrice: string;
          totalProfit: string;
        };
        title: string;
      };
      offerInstanceId: string;
      reservationConflict: {
        hint: string;
        interval: string;
        intervalSingle: string;
        offer: string;
        overlapType: Record<ReservationOverlapTypeDto, Record<'other' | 'sameOffer', string>>;
        title: string;
        unknownOverlap: string;
      };
      saveErrors: {
        codes: Record<OfferSaveErrorCode, string>;
        outsideTerm: {
          more: string;
          multiple: string;
          position: string;
          single: string;
        };
      };
      status: {
        acceptanceConflict: {
          checkAvailability: string;
          closeAriaLabel: string;
          configurationError: string;
          description: string;
          loading: string;
          offer: string;
          range: string;
          returnToEdit: string;
          title: string;
        };
        blockedReason: {
          guardRejected: string;
          unfinishedTasks: string;
          unsupportedTransition: string;
        };
        button: {
          ariaLabel: string;
          changeStatus: string;
        };
        error: string;
        menuAriaLabel: string;
        options: {
          accepted: { description: string };
          default: { description: string };
          draft: { description: string };
          inProgress: { description: string };
          rejected: { description: string };
          toAccept: { description: string };
        };
        requirements: {
          closeAriaLabel: string;
          components: { listTitle: string };
          hint: { both: string; components: string; tasks: string };
          returnToEdit: string;
          tasks: { assignee: string; listTitle: string; unassigned: string };
          title: string;
        };
        success: string;
        transitionKind: {
          backward: string;
          final: string;
          forward: string;
        };
      };
      summary: {
        currency: string;
        offerDefinitionId: string;
        statusLabel: string;
        totalExternalPrice: string;
        totalInternalPrice: string;
        totalProfit: string;
        workflowCode: string;
      };
      tasks: {
        actions: string;
        add: string;
        assignee: string;
        dueDate: string;
        empty: string;
        groupOther: string;
        groupRequiredStatus: string;
        loadError: string;
        status: string;
        title: string;
        titleColumn: string;
        unassigned: string;
      };
      term: {
        errors: Record<
          Exclude<OfferInstanceTermErrorDetailsDtoCodeEnum, 'OFFER_RESERVATION_OUTSIDE_TERM'>,
          string
        >;
        event: string;
        from: string;
        saveError: string;
        to: string;
      };
    };
    empty: string;
    error: string;
    head: Record<'actions' | keyof Omit<ProcessOfferInstanceSummaryDto, 'statusCode'>, string>;
    title: string;
  };
  processContacts: {
    button: {
      cancel: string;
      submit: string;
    };
    contactOwner: string;
    noContactOwner: string;
    title: string;
    tooltip: {
      goToContact: string;
      manageContacts: string;
    };
  };
  tabs: Record<ProcessDetailsTab, string>;
  tabsNotes: string;
  tasks: {
    button: {
      markMessagesAsSeen: string;
    };
    dialog: {
      markMessagesAsSeen: {
        button: {
          cancel: string;
          confirm: string;
        };
        content: string;
        title: string;
      };
    };
    offer: string;
    title: string;
  };
  toDoInfo: {
    allGood: string;
    requiresAttention: string;
  };
};

export const processesDetailsTranslation: ProcessesDetailsTranslation = {
  addTask: {
    button: {
      open: 'Dodaj zadanie',
    },
  },
  customer: {
    name: 'Klient',
    noCustomerSelected: 'Nie przypisano klienta do tego procesu',
    tooltip: {
      goToCustomer: 'Przejdź do klienta',
    },
    type: 'Typ',
    typeOptions: customerTypeOptionsTranslation,
  },
  header: {
    button: {
      edit: 'Edycja',
    },
    calendarEntryQuery: {
      button: {
        cancel: 'Anuluj',
        clear: 'Wyczyść zapytany termin/zasób',
        edit: 'Edytuj zapytany termin/zasób',
        editBookingModeError:
          'Nie udało się wczytać trybu rezerwacji typu słownika. Zapytanego terminu nie można edytować.',
        save: 'Zapisz',
      },
      dialogTitle: 'Zapytany termin/zasób',
      summary: {
        resourceWithRange: '{{resource}} — {{range}}',
        typeWithEntry: '{{type}}: {{entry}}',
      },
      title: 'Zapytany termin/zasób',
      withoutRange: 'termin do ustalenia',
    },
    nextStatus: {
      [ProcessStatusDto.Canceled]: 'Anuluj',
      [ProcessStatusDto.Created]: 'Utwórz',
      [ProcessStatusDto.Finished]: 'Zakończ',
      [ProcessStatusDto.InProgress]: 'Rozpocznij',
    },
    status: 'Status',
    statusOptions: processStatusOptionsTranslation,
  },
  main: {
    createdAt: 'Utworzony',
    customerContacts: 'Kontakty klienta',
    processCreator: 'Twórca procesu',
    processOwner: 'Właściciel procesu',
    status: 'Status',
    updatedAt: 'Ostatnia aktualizacja',
  },
  offerInstances: {
    actions: {
      open: 'Otwórz',
    },
    create: {
      button: {
        cancel: 'Anuluj',
        create: 'Utwórz',
      },
      offerDefinition: 'Definicja oferty',
      openButton: 'Nowa oferta',
      title: 'Nowa oferta',
    },
    details: {
      backAriaLabel: 'Wróć do procesu',
      button: {
        cancel: 'Anuluj',
        save: 'Zapisz',
      },
      calendarEntryQueryMismatch: {
        entry:
          'Element słownika: zapytanie dotyczy wpisu „{{query}}”, w ofercie jest wpis „{{offer}}”.',
        entryMany:
          'Element słownika: zapytanie dotyczy wpisu „{{query}}”, w ofercie są wpisy: {{offer}}.',
        hint: 'Sprawdź, czy rozbieżność jest zamierzona. Ostrzeżenie nie blokuje zapisu oferty.',
        term: 'Termin imprezy: zapytanie dotyczy {{query}}, w ofercie jest {{offer}}.',
        title: 'Oferta różni się od zapytania na procesie',
      },
      components: {
        addDictionaryItem: 'Dodaj wariant',
        addFileItem: 'Dodaj plik',
        addItem: 'Dodaj pozycję',
        addTextItem: 'Dodaj treść',
        assembly: {
          availability: {
            assembly: 'Sprawdź dostępność montażu',
            disassembly: 'Sprawdź dostępność demontażu',
          },
          description: {
            assembly: 'Opis montażu',
            disassembly: 'Opis demontażu',
          },
          from: 'Początek montażu',
          hint: 'Opcjonalne. Montaż może zaczynać się przed terminem imprezy, a demontaż kończyć po nim.',
          readFrom: 'Montaż od {{time}}',
          readTo: 'Demontaż do {{time}}',
          title: 'Montaż i demontaż',
          to: 'Koniec demontażu',
        },
        assignedPerson: 'Osoba odpowiedzialna',
        assigneeTypeSupervisor: 'opiekun',
        assigneeTypeUser: 'użytkownik',
        availability: {
          actions: 'Akcje',
          available: 'Dostępne',
          clearFilters: 'Wyczyść filtry',
          close: 'Zamknij',
          closeDialog: 'Zamknij okno dostępności',
          empty: 'Brak dostępnych pozycji.',
          emptyFiltered: 'Brak wyników dla wybranych filtrów.',
          filterElements: 'Elementy',
          filters: 'Filtry',
          filterStatus: 'Status',
          from: 'Początek okresu',
          hintDetails: 'Najedź na kropkę rezerwacji, aby zobaczyć szczegóły.',
          item: 'Element słownika',
          loadError: 'Nie udało się pobrać dostępności.',
          nextRange: 'Następny zakres',
          prevRange: 'Poprzedni zakres',
          rangeOrder: 'Początek zakresu musi być wcześniejszy niż koniec',
          rangeOutsideTerm: 'Zakres musi mieścić się w terminie imprezy',
          rangeRequired: 'Uzupełnij poprawny początek i koniec okresu',
          refresh: 'Odśwież',
          reservation: {
            more: '+{{count}} więcej',
            occupied: 'Zajęty',
            statusShort: {
              confirmed: 'Potwierdzona',
              tentative: 'Wstępna',
            },
          },
          retry: 'Spróbuj ponownie',
          select: 'Wybierz',
          selectBlocked: 'Element ma rezerwację potwierdzoną w wybranym zakresie.',
          selectBlockedByWindow:
            'Element jest zajęty montażem lub demontażem innej oferty w wybranym zakresie.',
          sharedReservationsWarning:
            'Zasób jest już zarezerwowany w innej ofercie w tym zakresie. Wybór nie jest blokowany.',
          status: {
            confirmed: 'Rezerwacja potwierdzona',
            free: 'Wolny',
            tentative: 'Rezerwacja wstępna',
            windowOnly: 'Tylko montaż lub demontaż',
          },
          subtitle: 'Sprawdź dostępność elementów i zarezerwuj termin.',
          subtitleAssembly:
            'Wybierz początek montażu tego elementu. Koniec zakresu to początek rezerwacji pozycji. Dzień zajęty tylko montażem lub demontażem innej oferty jest dla montażu wolny.',
          subtitleDisassembly:
            'Wybierz koniec demontażu tego elementu. Początek zakresu to koniec rezerwacji pozycji. Dzień zajęty tylko montażem lub demontażem innej oferty jest dla demontażu wolny.',
          tableAriaLabel: 'Porównanie dostępności',
          tentativeWarning: 'Rezerwacja wstępna nie blokuje wyboru, ale może zostać potwierdzona.',
          termRequired: 'Ustaw termin imprezy, aby sprawdzić dostępność',
          title: 'Dostępność',
          to: 'Koniec okresu',
          today: 'Dzisiaj',
        },
        changeFile: 'Zmień',
        checkAvailability: 'Sprawdź dostępność',
        complete: 'Uzupełnione',
        componentsCount: 'Komponenty: {{count}}',
        deleteItemAriaLabel: 'Usuń element',
        dictionaryDescription: 'Warianty pojazdów i terminy ich rezerwacji',
        dictionaryItemType: 'Typ pojazdu',
        dictionaryValue: 'Wartość słownikowa',
        dictionaryVariants: 'Warianty pojazdu',
        editItem: 'Edytuj element',
        editItemAriaLabel: 'Edytuj element',
        empty: 'Ta oferta nie ma komponentów.',
        emptyFiltered: 'Brak komponentów dla wybranych filtrów.',
        enabled: 'Aktywny',
        externalPrice: 'Cena zewnętrzna',
        fileAdded: 'Plik dodano',
        fileDescription: 'Wymagane pliki do przygotowania oferty',
        fileDownload: 'Pobierz plik',
        fileEmpty: 'Brak pliku',
        fileRemove: 'Usuń plik',
        fileValue: 'Plik',
        fillFromOfferTerm: 'Wypełnij z terminu imprezy',
        fillText: 'Uzupełnij',
        inactive: 'Nieaktywny',
        inactiveItem: 'Pozycja nieaktywna',
        incomplete: 'Brak danych',
        internalPrice: 'Cena wewnętrzna',
        itemCancel: 'Anuluj',
        itemName: 'Nazwa elementu',
        itemSave: 'Zapisz',
        itemsTitle: 'Pozycje',
        itemValueEmpty: 'Brak wartości',
        lastChange: 'Ostatnia zmiana: {{date}}',
        missing: 'Brakujące: {{count}}',
        noReservation: 'Brak terminu rezerwacji',
        noText: 'Brak treści',
        prices: 'Ceny',
        progress: '{{completed}} z {{total}}',
        requiredDocuments: 'Wymagane dokumenty',
        requiredForNextStatus: 'Wymagane do zmiany statusu',
        requiredForNextStatusTooltip:
          'Ten komponent musi być uzupełniony, aby przejść na kolejny status oferty.',
        requiredForStatus: 'Wymagane dla statusu: {{status}}',
        reservationFrom: 'Początek rezerwacji',
        reservationTerm: 'Termin rezerwacji',
        reservationTo: 'Koniec rezerwacji',
        reservationValidation: {
          rangeOrder: 'Początek zakresu musi być wcześniejszy niż koniec',
          rangeOutsideTerm: 'Zakres pozycji musi mieścić się w terminie imprezy',
          rangeRequired: 'Uzupełnij początek i koniec zakresu',
          termRequired: 'Pozycja kalendarzowa wymaga ustawionego terminu imprezy',
        },
        returnToStatus: 'Aby włączyć lub wyłączyć, wróć do statusu {{status}}.',
        saveError: 'Nie udało się zapisać.',
        saveSuccess: 'Zapisano zmiany.',
        textDescription: 'Informacje tekstowe przekazywane wraz z zapytaniem',
        textFields: 'Pola tekstowe',
        textValue: 'Wartość tekstowa',
        valueType: {
          DICTIONARY: 'Słownik',
          FILE: 'Plik',
          TEXT: 'Tekst',
        },
      },
      header: {
        collapseAriaLabel: 'Zwiń szczegóły oferty',
        expandAriaLabel: 'Rozwiń szczegóły oferty',
        preview: {
          ariaLabel: 'Podsumowanie szczegółów oferty',
          eventTerm: 'Impreza: {{term}}',
          noEventTerm: 'Impreza: brak terminu',
          termErrors: 'Popraw termin w szczegółach oferty',
          totalExternalPrice: 'Zewn.: {{value}} {{currency}}',
          totalInternalPrice: 'Wewn.: {{value}} {{currency}}',
          totalProfit: 'Zysk: {{value}} {{currency}}',
        },
        title: 'Szczegóły oferty',
      },
      offerInstanceId: 'ID instancji oferty',
      reservationConflict: {
        hint: 'Przesuń czasy tak, aby nie nachodziły na zajęty przedział, i zapisz ponownie.',
        interval: 'Nakładający się przedział: {{from}} – {{to}}',
        intervalSingle: 'Nakładający się przedział: {{from}}',
        offer: 'Oferta {{id}}',
        overlapType: {
          ASSEMBLY_EVENT: {
            other: 'Twój montaż nachodzi na imprezę innej oferty',
            sameOffer: 'Twój montaż nachodzi na imprezę innej rezerwacji tej oferty',
          },
          DISASSEMBLY_EVENT: {
            other: 'Twój demontaż nachodzi na imprezę innej oferty',
            sameOffer: 'Twój demontaż nachodzi na imprezę innej rezerwacji tej oferty',
          },
          EVENT_ASSEMBLY: {
            other: 'Twoja impreza nachodzi na montaż innej oferty',
            sameOffer: 'Twoja impreza nachodzi na montaż innej rezerwacji tej oferty',
          },
          EVENT_DISASSEMBLY: {
            other: 'Twoja impreza nachodzi na demontaż innej oferty',
            sameOffer: 'Twoja impreza nachodzi na demontaż innej rezerwacji tej oferty',
          },
          EVENT_EVENT: {
            other: 'Twoja impreza nachodzi na imprezę innej oferty',
            sameOffer: 'Twoja impreza nachodzi na imprezę innej rezerwacji tej oferty',
          },
        },
        title: 'Nie zapisano zmian: wybrany zasób jest zajęty w tym terminie',
        unknownOverlap: 'Zasób jest już zajęty przez potwierdzoną rezerwację.',
      },
      saveErrors: {
        codes: {
          OFFER_COMPONENT_INTERNAL_COST_REQUIRED:
            'Podaj koszt wewnętrzny komponentu: jest potrzebny do wyliczenia prowizji.',
          OFFER_COMPONENT_NOT_FOUND:
            'Ten komponent nie istnieje już w ofercie. Odśwież widok i spróbuj ponownie.',
          OFFER_DEFINITION_INACTIVE: 'Wybrana definicja oferty jest nieaktywna. Wybierz inną.',
          OFFER_ITEM_ASSEMBLY_DESCRIPTION_WITHOUT_ASSEMBLY:
            'Opis montażu jest możliwy tylko wtedy, gdy montaż zaczyna się przed rezerwacją pozycji. Ustaw wcześniejszy początek montażu albo usuń opis.',
          OFFER_ITEM_ASSEMBLY_INVALID_ORDER:
            'Początek montażu nie może być późniejszy niż początek rezerwacji.',
          OFFER_ITEM_ASSEMBLY_NOT_SUPPORTED:
            'Pozycje tego typu nie mają montażu, bo typ słownika nie ma włączonej opcji „Wymaga montażu”. Usuń montaż i jego opis.',
          OFFER_ITEM_ASSEMBLY_REQUIRES_RESERVATION:
            'Montaż wymaga rezerwacji pozycji. Ustaw termin rezerwacji albo usuń montaż.',
          OFFER_ITEM_DISASSEMBLY_DESCRIPTION_WITHOUT_DISASSEMBLY:
            'Opis demontażu jest możliwy tylko wtedy, gdy demontaż kończy się po rezerwacji pozycji. Ustaw późniejszy koniec demontażu albo usuń opis.',
          OFFER_ITEM_DISASSEMBLY_INVALID_ORDER:
            'Koniec demontażu nie może być wcześniejszy niż koniec rezerwacji.',
          OFFER_ITEM_DISASSEMBLY_NOT_SUPPORTED:
            'Pozycje tego typu nie mają demontażu, bo typ słownika nie ma włączonej opcji „Wymaga montażu”. Usuń demontaż i jego opis.',
          OFFER_ITEM_DISASSEMBLY_REQUIRES_RESERVATION:
            'Demontaż wymaga rezerwacji pozycji. Ustaw termin rezerwacji albo usuń demontaż.',
          OFFER_ITEM_NAME_REQUIRED: 'Podaj nazwę pozycji.',
          OFFER_ITEM_NOT_FOUND:
            'Ta pozycja nie istnieje już w ofercie. Odśwież widok i spróbuj ponownie.',
          OFFER_ITEM_PREDEFINED_NOT_DELETABLE: 'Pozycji predefiniowanej nie można usunąć.',
          OFFER_ITEM_VALUE_INVALID:
            'Wartość pozycji nie pasuje do jej typu. Uzupełnij tekst, wybierz element słownika albo dodaj plik.',
          OFFER_RESERVATION_CONFIRMED_LOCKED:
            'Rezerwacja tej pozycji jest potwierdzona, więc nie można zmienić jej elementu ani terminu.',
          OFFER_RESERVATION_DICTIONARY_INACTIVE:
            'Wybrany element słownika jest nieaktywny, więc nie można dla niego zarezerwować terminu. Wybierz inny element.',
          OFFER_RESERVATION_OUTSIDE_TERM:
            'Rezerwacja pozycji wykracza poza termin imprezy. Zmień termin rezerwacji w pozycji („Sprawdź dostępność”), a potem zapisz ponownie.',
          OFFER_RESERVATION_RANGE_INCOMPLETE: 'Uzupełnij początek i koniec rezerwacji.',
          OFFER_RESERVATION_RANGE_INVALID_ORDER:
            'Początek rezerwacji musi być wcześniejszy niż jej koniec.',
          OFFER_TERM_REQUIRED_FOR_RESERVATION:
            'Rezerwacja wymaga terminu imprezy. Ustaw i zapisz termin imprezy w ofercie, a potem zapisz pozycję.',
        },
        outsideTerm: {
          more: 'oraz pozostałe pozycje: {{count}}',
          multiple:
            'Rezerwacje pozycji wykraczają poza termin imprezy: {{positions}}. Zmień termin rezerwacji w tych pozycjach („Sprawdź dostępność”), a potem zapisz ponownie.',
          position: '„{{name}}” (komponent „{{component}}”)',
          single:
            'Rezerwacja pozycji {{position}} wykracza poza termin imprezy. Zmień termin rezerwacji w pozycji („Sprawdź dostępność”), a potem zapisz ponownie.',
        },
      },
      status: {
        acceptanceConflict: {
          checkAvailability: 'Sprawdź dostępność',
          closeAriaLabel: 'Zamknij komunikat',
          configurationError: 'Nie udało się pobrać konfiguracji kalendarzy.',
          description: 'Wykryto konflikty wymagające rozwiązania.',
          loading: 'Ładowanie konfiguracji kalendarzy',
          offer: 'Oferta {{id}}',
          range: 'Potwierdzona rezerwacja: {{from}} – {{to}}',
          returnToEdit: 'Wróć do edycji',
          title: 'Nie można zaakceptować oferty',
        },
        blockedReason: {
          guardRejected: 'Przejście zablokowane przez reguły workflow.',
          unfinishedTasks: 'Przejście zablokowane przez niezakończone zadania:',
          unsupportedTransition: 'To przejście statusu jest niedozwolone.',
        },
        button: {
          ariaLabel: 'Aktualny status oferty: {{status}}. Zmień status',
          changeStatus: 'Zmień status',
        },
        error: 'Nie udało się zmienić statusu oferty.',
        menuAriaLabel: 'Lista statusów oferty',
        options: {
          accepted: { description: 'Oferta została zaakceptowana' },
          default: { description: 'Status oferty' },
          draft: { description: 'Oferta jest zapisywana jako szkic' },
          inProgress: { description: 'Oferta jest w trakcie realizacji' },
          rejected: { description: 'Oferta została odrzucona' },
          toAccept: { description: 'Oferta oczekuje na akceptację' },
        },
        requirements: {
          closeAriaLabel: 'Zamknij komunikat',
          components: { listTitle: 'Komponenty wymagające uzupełnienia' },
          hint: {
            both: 'Uzupełnij dane we wskazanych komponentach i zakończ wskazane zadania, a następnie ponownie zmień status oferty.',
            components:
              'Uzupełnij dane we wskazanych komponentach, a następnie ponownie zmień status oferty.',
            tasks: 'Zakończ wskazane zadania, a następnie ponownie zmień status oferty.',
          },
          returnToEdit: 'Wróć do edycji',
          tasks: {
            assignee: 'Przypisany: {{name}}',
            listTitle: 'Niezakończone zadania',
            unassigned: 'Bez przypisanego',
          },
          title: 'Nie można zmienić statusu oferty',
        },
        success: 'Status zmieniono na: {{status}}',
        transitionKind: {
          backward: 'Cofnij',
          final: 'Finalny',
          forward: 'Do przodu',
        },
      },
      summary: {
        currency: 'PLN',
        offerDefinitionId: 'ID definicji oferty',
        statusLabel: 'Status',
        totalExternalPrice: 'Łączna cena zewnętrzna',
        totalInternalPrice: 'Łączna cena wewnętrzna',
        totalProfit: 'Zysk',
        workflowCode: 'Workflow',
      },
      tasks: {
        actions: 'Akcje',
        add: 'Dodaj zadanie',
        assignee: 'Przypisany',
        dueDate: 'Termin',
        empty: 'Ta oferta nie ma jeszcze zadań.',
        groupOther: 'Pozostałe',
        groupRequiredStatus: 'Wymagane do statusu: {{status}}',
        loadError: 'Nie udało się wczytać zadań oferty.',
        status: 'Status',
        title: 'Zadania',
        titleColumn: 'Tytuł',
        unassigned: 'Bez przypisanego',
      },
      term: {
        errors: {
          OFFER_ACCEPTED: 'Oferta jest zaakceptowana. Wycofaj akceptację, aby zmienić jej termin.',
          OFFER_TERM_INCOMPLETE: 'Podaj początek i koniec imprezy albo zostaw oba pola puste',
          OFFER_TERM_INVALID_ORDER: 'Początek imprezy musi być wcześniejszy niż jej koniec',
        },
        event: 'Termin imprezy',
        from: 'Początek imprezy',
        saveError: 'Nie udało się zapisać terminu imprezy',
        to: 'Koniec imprezy',
      },
    },
    empty: 'Brak nowych ofert w tym procesie.',
    error: 'Nie udało się pobrać nowych ofert.',
    head: {
      actions: 'Akcje',
      offerDefinitionId: 'ID definicji',
      offerInstanceId: 'ID instancji',
      statusLabel: 'Status',
      totalExternalPrice: 'Cena zewnętrzna',
      totalInternalPrice: 'Cena wewnętrzna',
      workflowCode: 'Workflow',
    },
    title: 'Nowe oferty',
  },
  processContacts: {
    button: {
      cancel: 'Anuluj',
      submit: 'Zapisz',
    },
    contactOwner: 'Opiekun kontaktu',
    noContactOwner: 'Brak opiekuna',
    title: 'Osoby do kontaktu',
    tooltip: {
      goToContact: 'Przejdź do kontaktu',
      manageContacts: 'Wybierz osoby do kontaktu',
    },
  },
  tabs: {
    [ProcessDetailsTab.FILES]: 'Pliki',
    [ProcessDetailsTab.MESSAGES]: 'Maile i notatki',
    [ProcessDetailsTab.OFFER_INSTANCES]: 'Oferty',
    [ProcessDetailsTab.TASKS]: 'Zadania',
  },
  tabsNotes: 'Notatki',
  tasks: {
    button: {
      markMessagesAsSeen: 'Potwierdź przeczytanie wiadomości',
    },
    dialog: {
      markMessagesAsSeen: {
        button: {
          cancel: 'Anuluj',
          confirm: 'Potwierdź',
        },
        content:
          'Potwierdzenie tej akcji spowoduje zamknięcie wszystkich zadań w procesie dotyczących wiadomości.',
        title: 'Potwierdź przeczytanie wiadomości',
      },
    },
    offer: 'Oferta {{offerBusinessId}}',
    title: 'Zadania',
  },
  toDoInfo: {
    allGood: 'Wszystko ok',
    requiresAttention: 'Proces wymaga uwagi',
  },
};
